import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  inject,
  OnChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { AnalyticsService } from '@digital-platform/rs-analytics/services';
import { Wayfinder, WayfinderJson } from '@digital-platform/rs-cms/domain';
import { ViewportService } from '@digital-platform/rs-shared/services';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { CtaButtonComponent } from '../cta-button/cta-button.component';

interface Step {
  isDropdownOpen: boolean;
  isDropdownActive: boolean;
  selectedOption: WayfinderJson | null;
  hoveredOption: WayfinderJson | null;
}

interface Steps {
  [key: string]: Step;
  WHO: Step;
  SERVICE: Step;
  LOOKING_FOR: Step;
}

interface WayfinderAngularConfig {
  mobileClass?: string;
  midsizeClass?: string;
  desktopClass?: string;
}

@Component({
  selector: 'digital-platform-wayfinder',
  standalone: true,
  imports: [CommonModule, CtaButtonComponent],
  templateUrl: './wayfinder.component.html',
  styleUrls: ['./wayfinder.component.scss'],
})
export class WayfinderComponent
  extends BasePTypeComponent
  implements Wayfinder, OnDestroy, OnChanges
{
  public override type = 'wayfinderMatrix' as const;

  @Input() public wayfinderMatrixJson: WayfinderJson[] = [];
  @Input() public angularConfig: WayfinderAngularConfig | null = null;

  public url = [
    {
      uri: '',
      title: 'Go',
      options: {
        visibilityClass: {},
        attributes: {
          target: 0,
          class: 'button_lg',
        },
      },
    },
  ];

  public defaultButtonStyle = {
    tid: 6076,
    name: 'Primary (ADA)',
    ctaStyle: 'btn-primary',
  };
  public defaultButtonSize = {
    tid: 6141,
    name: 'L',
    ctaSize: 'btn-lg',
  };

  @ViewChild('whoDropdown') whoDropdown!: ElementRef;
  @ViewChild('serviceDropdown') serviceDropdown!: ElementRef;
  @ViewChild('lookingForDropdown') lookingForDropdown!: ElementRef;

  public steps: Steps = {
    WHO: {
      isDropdownOpen: false,
      isDropdownActive: false,
      selectedOption: null,
      hoveredOption: null,
    },
    SERVICE: {
      isDropdownOpen: false,
      isDropdownActive: false,
      selectedOption: null,
      hoveredOption: null,
    },
    LOOKING_FOR: {
      isDropdownOpen: false,
      isDropdownActive: false,
      selectedOption: null,
      hoveredOption: null,
    },
  };

  public wayfinderClass = '';

  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private analyticsService = inject(AnalyticsService);
  private sanitizer = inject(DomSanitizer);
  private viewportService = inject(ViewportService);

  @HostListener('document:click', ['$event'])
  onOutsideDropdownClick(event: MouseEvent) {
    if (
      !this.whoDropdown.nativeElement.contains(event.target) &&
      !this.serviceDropdown.nativeElement.contains(event.target) &&
      !this.lookingForDropdown.nativeElement.contains(event.target)
    ) {
      this.closeAllDropdowns();
    }
  }

  public ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.onOutsideDropdownClick);
    }
  }

  ngOnChanges() {
    this.wayfinderClass = this.concatWayfinderClasses();
  }

  private concatWayfinderClasses(): string {
    const cls = ['wayfinder'];
    if (this.angularConfig) {
      const { desktopClass, mobileClass, midsizeClass } = this.angularConfig;
      if (this.viewportService.isMobile) {
        if (mobileClass) cls.push(mobileClass);
      } else if (this.viewportService.isMidsize) {
        if (midsizeClass) cls.push(midsizeClass);
      } else if (this.viewportService.isDesktop) {
        if (desktopClass) cls.push(desktopClass);
      }
    }
    return cls.join(' ');
  }

  cleanHtml(text = ''): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(text);
  }

  toggleDropdown(step: string): void {
    const isOpen = this.steps[step].isDropdownOpen;
    this.closeAllDropdowns();
    if (!isOpen) {
      this.steps[step].isDropdownOpen = true;
    }
  }

  selectOption(step: string, option: any): void {
    switch (step) {
      case 'WHO':
        this.steps['SERVICE'].selectedOption = null;
        this.steps['LOOKING_FOR'].selectedOption = null;
        break;
      case 'SERVICE':
        this.steps['LOOKING_FOR'].selectedOption = null;
        break;
    }
    this.steps[step].selectedOption = option;
    if (step === 'WHO') {
      this.analyticsService.trackWayfinderStart();
    }
    this.closeAllDropdowns();
  }

  closeAllDropdowns() {
    for (const key in this.steps) {
      if (this.steps.hasOwnProperty(key)) {
        this.steps[key].isDropdownOpen = false;
      }
    }
  }

  getCurrentOpenDropdown(): any {
    return Object.values(this.steps).find((step: any) => step.isDropdownOpen);
  }

  get isFormValid(): boolean {
    return Object.values(this.steps).every((step: any) => step.selectedOption);
  }

  onKeyUp(event: any, step: string): void {
    const hoveredOption = this.steps[step].hoveredOption;

    switch (event.key) {
      case 'Enter':
        if (hoveredOption) {
          this.selectOption(step, hoveredOption);
        } else {
          this.toggleDropdown(step);
        }
        break;
      case 'Escape':
        this.toggleDropdown(step);
        break;
    }
  }

  onGo(): void {
    const wayfinderDetails = {
      who: this.steps['WHO']?.selectedOption?.term || '',
      whatService: this.steps['SERVICE']?.selectedOption?.term || '',
      lookingFor: this.steps['LOOKING_FOR']?.selectedOption?.term || '',
    };
    this.analyticsService.trackWayfinderComplete(wayfinderDetails);
    this.router.navigate([
      this.steps['LOOKING_FOR'].selectedOption?.field_destination_url,
    ]);
  }
}
